/* eslint-disable @typescript-eslint/ban-ts-comment */

import { RootState } from '@/store'
import * as amplitude from '@amplitude/analytics-browser'
import { BaseQueryFn, FetchArgs, FetchBaseQueryError, fetchBaseQuery } from '@reduxjs/toolkit/query'
import { Mutex } from 'async-mutex'
import { config as appConfig } from '../../core'
// Imported from the slice module, not the feature barrel: the barrel pulls in components and
// the store, which import the generated api modules, which import this file's consumer
// `config/api.ts` - a cycle that crashes whichever module is evaluated first.
import { showLoggedOutModal } from '../../features/auth/authSlice'
import { Detail } from './tenant/user'

const baseUrl = appConfig.api.origin
const SIGNATURE_EXPIRED_MSG = 'Authentication failed'

// Create a new mutex
const mutex = new Mutex()

const baseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers) => {
    headers.set('Accept', 'application/json')
    headers.set('Cache-Control', 'no-cache')
    headers.set('Pragma', 'no-cache')
    headers.set('Expires', '0')
    // todo: get selected language from redux and automatically send as request header
    // headers.set('x-language-tag', 'de-DE')
    return headers
  },
  paramsSerializer: (params) => {
    const searchParams = new URLSearchParams()
    for (const key in params) {
      const value = params[key]
      if (value !== undefined) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            searchParams.append(`${key}`, item)
          })
        } else {
          searchParams.append(key, value)
        }
      }
    }
    return searchParams.toString()
  },
  credentials: 'include',
})

// @ts-ignore
const mutexCustomFetchBase: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  // wait until the mutex is available without locking it
  await mutex.waitForUnlock()

  const result = await baseQuery(args, api, extraOptions)

  if (result.error) {
    const errorDetail = result.error.data as Detail

    if (result.error.status == 401) {
      const state = api.getState() as RootState
      const user = state.authSlice?.user

      if (errorDetail.detail === SIGNATURE_EXPIRED_MSG && user) {
        amplitude.reset()
        api.dispatch(showLoggedOutModal())
      } else {
        // Redirecting to `/login` to avoid presenting the log out page to
        // a user who hasn't been previously logged in. This will happen when
        // the backend can't find the session cookie.
        const urlSearchParams = new URLSearchParams(window.location.search)
        const userEmail = urlSearchParams.get('username')
        window.location.href = `${appConfig.api.origin}/login?redirect_url=${encodeURIComponent(
          window.location.href,
        )}&username=${encodeURIComponent(userEmail)}`
      }
    }
  }

  return result
}

export const authCustomFetchBase = mutexCustomFetchBase
